import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import currentAccountService from '../services/currentAccountService';

const CurrentAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hideZeroBalance, setHideZeroBalance] = useState(false);
  const [balanceFilter, setBalanceFilter] = useState('all'); // 'all', 'borclu', 'alacakli'
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);


  const fetchAccounts = async () => {
    try {
      const data = await currentAccountService.getAll();
      setAccounts(data);
    } catch (error) {
      toast.error('Cariler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleDelete = async (id) => {
    if (window.confirm('Bu cari hesabı silmek istediğinize emin misiniz?')) {
      try {
        await currentAccountService.delete(id);
        toast.success('Cari hesap silindi.');
        fetchAccounts();
      } catch (error) {
        toast.error('Silme işlemi başarısız.');
      }
    }
  };

  const filteredAccounts = accounts.filter(account => {
    const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
    const matchesSearch = account.company_name?.toLocaleLowerCase('tr-TR').includes(searchLower) ||
      account.authorized_person?.toLocaleLowerCase('tr-TR').includes(searchLower);

    const balanceValue = parseFloat(account.balance) || 0;

    // 0 Bakiye Gizle Filtresi
    if (hideZeroBalance) {
      if (Math.abs(balanceValue) < 0.005) return false;
    }

    // Borçlu / Alacaklı Filtresi
    if (balanceFilter === 'borclu') {
      // Bize borcu olanlar (Müşteri > 0, Tedarikçi < 0)
      if (account.account_type === 'musteri' && balanceValue <= 0.005) return false;
      if (account.account_type === 'tedarikci' && balanceValue >= -0.005) return false;
    } else if (balanceFilter === 'alacakli') {
      // Bizim borcumuz olanlar (Müşteri < 0, Tedarikçi > 0)
      if (account.account_type === 'musteri' && balanceValue >= -0.005) return false;
      if (account.account_type === 'tedarikci' && balanceValue <= 0.005) return false;
    }

    return matchesSearch;
  });

  // Toplamları Hesapla (Filtrelenmiş listeye göre)
  const { totalReceivable, totalPayable } = filteredAccounts.reduce((acc, account) => {
    const balanceValue = parseFloat(account.balance) || 0;
    let displayBalance = account.account_type === 'tedarikci' ? -balanceValue : balanceValue;
    
    if (Math.abs(displayBalance) < 0.005) displayBalance = 0;

    if (displayBalance > 0) {
      acc.totalReceivable += displayBalance;
    } else {
      acc.totalPayable += Math.abs(displayBalance);
    }
    return acc;
  }, { totalReceivable: 0, totalPayable: 0 });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <FaChevronUp className="ms-1 small" /> : <FaChevronDown className="ms-1 small" />;
  };

  const sortedAccounts = React.useMemo(() => {
    let sortableItems = [...filteredAccounts];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'balance') {
          const aBal = parseFloat(a.balance) || 0;
          aVal = a.account_type === 'tedarikci' ? -aBal : aBal;
          if (Math.abs(aVal) < 0.005) aVal = 0;

          const bBal = parseFloat(b.balance) || 0;
          bVal = b.account_type === 'tedarikci' ? -bBal : bBal;
          if (Math.abs(bVal) < 0.005) bVal = 0;
        } else {
          if (aVal === null || aVal === undefined) aVal = '';
          if (bVal === null || bVal === undefined) bVal = '';
          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredAccounts, sortConfig]);

  if (loading) return <div>Yükleniyor...</div>;


  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Cari Hesaplar</h2>
        <Link to="/current-accounts/new" className="btn btn-primary">
          <FaPlus className="me-2" /> Yeni Cari Ekle
        </Link>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 mb-2 mb-md-0">
          <input
            type="text"
            className="form-control"
            placeholder="Cari adı veya yetkili kişi ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-6 d-flex align-items-center gap-3 justify-content-md-end">
          <select 
            className="form-select" 
            value={balanceFilter} 
            onChange={(e) => setBalanceFilter(e.target.value)}
            style={{ maxWidth: '160px' }}
          >
            <option value="all">Tümü</option>
            <option value="borclu">Borçlular</option>
            <option value="alacakli">Alacaklılar</option>
          </select>

          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="hideZeroBalance"
              checked={hideZeroBalance}
              onChange={(e) => setHideZeroBalance(e.target.checked)}
            />
            <label className="form-check-label text-nowrap" htmlFor="hideZeroBalance">
              Bakiyesi 0 olanları gizle
            </label>
          </div>
        </div>
      </div>

      <div className="card shadow-lg border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <div className="card-body">
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('company_name')}>Firma Adı {getSortIcon('company_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('authorized_person')}>Yetkili {getSortIcon('authorized_person')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('account_type')}>Tip {getSortIcon('account_type')}</th>
                  <th>Telefon</th>
                  <th className="text-end" style={{cursor: 'pointer'}} onClick={() => handleSort('balance')}>Güncel Bakiye {getSortIcon('balance')}</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {sortedAccounts.map((account) => {
                  // Eğer tedarikçi ise ve bakiye pozitifse (biz borçluyuz), eksi göster.
                  // Eğer müşteri ise ve bakiye pozitifse (o bize borçlu), artı göster.
                  // Eğer backend'den balance gelmezse 0 kabul et.
                  const balanceValue = parseFloat(account.balance) || 0;
                  let displayBalance = account.account_type === 'tedarikci' ? -balanceValue : balanceValue;
                  
                  // Eğer bakiye 0 ise (veya çok küçükse), -0.00 görünümünü engellemek için 0 yap
                  if (Math.abs(displayBalance) < 0.005) {
                    displayBalance = 0;
                  }
                  
                  return (
                  <tr key={account.id}>
                    <td>
                      <Link to={`/current-accounts/detail/${account.id}`} className="text-decoration-none text-dark">
                        {account.company_name}
                      </Link>
                    </td>
                    <td>{account.authorized_person}</td>
                    <td>
                      <span className={`badge ${account.account_type === 'musteri' ? 'bg-info' : 'bg-warning'}`}>
                        {account.account_type === 'musteri' ? 'Müşteri' : 'Tedarikçi'}
                      </span>
                    </td>
                    <td>{account.phone}</td>
                    <td className={`text-end fw-bold ${displayBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(displayBalance)}
                    </td>
                    <td>
                      <Link to={`/current-accounts/detail/${account.id}`} className="btn btn-sm btn-outline-info me-2" title="Hareketler">
                        <FaEye />
                      </Link>
                      <Link to={`/current-accounts/edit/${account.id}`} className="btn btn-sm btn-outline-primary me-2">
                        <FaEdit />
                      </Link>
                      <button onClick={() => handleDelete(account.id)} className="btn btn-sm btn-outline-danger">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
                })}
                {filteredAccounts.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center">Kayıtlı cari hesap bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobil Görünüm (Akordiyon Kartlar) */}
          <div className="d-md-none">
            {sortedAccounts.map((account) => {
              const balanceValue = parseFloat(account.balance) || 0;
              let displayBalance = account.account_type === 'tedarikci' ? -balanceValue : balanceValue;
              if (Math.abs(displayBalance) < 0.005) displayBalance = 0;

              return (
                <div className="card mb-3 shadow-sm" key={account.id}>
                  <div className="card-body" onClick={() => toggleRow(account.id)} style={{ cursor: 'pointer' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0 fw-bold">{account.company_name}</h6>
                        <small className="text-muted">{account.account_type === 'musteri' ? 'Müşteri' : 'Tedarikçi'}</small>
                      </div>
                      <div className="text-end">
                        <div className={`fw-bold ${displayBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(displayBalance)}
                        </div>
                        <small className="text-muted">{expandedRow === account.id ? <FaChevronUp /> : <FaChevronDown />}</small>
                      </div>
                    </div>

                    {expandedRow === account.id && (
                      <div className="mt-3 border-top pt-3">
                        <p className="mb-1"><strong>Yetkili:</strong> {account.authorized_person || '-'}</p>
                        <p className="mb-1"><strong>Telefon:</strong> {account.phone || '-'}</p>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Link to={`/current-accounts/detail/${account.id}`} className="btn btn-sm btn-outline-info">
                            <FaEye /> Detay
                          </Link>
                          <Link to={`/current-accounts/edit/${account.id}`} className="btn btn-sm btn-outline-primary">
                            <FaEdit /> Düzenle
                          </Link>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(account.id); }} className="btn btn-sm btn-outline-danger">
                            <FaTrash /> Sil
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Toplamlar Özeti */}
          <div className="mt-4 p-3 bg-light rounded border d-flex flex-column flex-md-row justify-content-end gap-3 gap-md-5">
            <div className="text-end">
              <h6 className="mb-1 text-muted">Toplam Alacak</h6>
              <h4 className="fw-bold text-success mb-0">
                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalReceivable)}
              </h4>
              <small className="text-muted" style={{ fontSize: '0.75rem' }}>(Bize Borçlu Olanlar)</small>
            </div>
            <div className="text-end">
              <h6 className="mb-1 text-muted">Toplam Borç</h6>
              <h4 className="fw-bold text-danger mb-0">
                {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalPayable)}
              </h4>
              <small className="text-muted" style={{ fontSize: '0.75rem' }}>(Bizim Borcumuz)</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentAccountsPage;
